import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { BaseDataService, HttpResponsesHandlersFactory } from "@bbwt/modules/data-service";
import { IChartPdfTemplateSettings } from "@main/reporting.v3/widget.chart/widget-chart.models";
import { HtmlRenderingService } from "@main/reporting.v3/core/rendering/html-rendering.service";
import { WidgetChartAuxiliaryService } from "@main/reporting.v3/widget.chart/api/widget-chart-auxiliary.service";
import { JsReportConfiguration } from "@main/pdf-exporting/js-report/js-report.models";
import { PdfExportSettings } from "@main/reporting.v3/core/reporting-models";
import { Chart } from "chart.js";

@Injectable()
export class WidgetChartPdfExportingService extends BaseDataService {
    readonly url: string = "api/reporting3/widget/chart";

    constructor(private readonly widgetChartAuxiliaryService: WidgetChartAuxiliaryService,
                private readonly htmlRenderingService: HtmlRenderingService,
                http: HttpClient, handlersFactory: HttpResponsesHandlersFactory) {
        super(http, handlersFactory);
    }

    public async requestPdfExport(widgetSourceId: string, settings: PdfExportSettings): Promise<string> {
        return this.httpPost(`${widgetSourceId}/pdf-export`, settings);
    }

    public async getPdfConfiguration(settings: IChartPdfTemplateSettings): Promise<JsReportConfiguration> {
        const template: string = this.getHtmlContent(settings);
        const cssRules: string = this.getCssRules();

        const footerTemplate: string = this.getFooterTemplate();
        const footerCssRules: string = this.getFooterCssRules();

        return {
            template: template,
            cssRules: cssRules,
            footerTemplate: footerTemplate,
            footerCssRules: footerCssRules,
            marginTop: 20,
            marginLeft: 20,
            marginRight: 20,
            marginBottom: 40,
        };
    }

    private getHtmlContent(settings: IChartPdfTemplateSettings): string {
        if (!settings.chart?.chart) return "";

        const chart: Chart = settings.chart.chart;
        const scale: number = window.devicePixelRatio;

        // Apply reverse pixel ratio only if it's greater than 1 (i.e, device pixel ratio is lower than 1).
        chart.options.devicePixelRatio = 0 < scale && scale < 1 ? 1 / scale : 1;
        chart.resize();
        chart.update();

        const parser: DOMParser = new DOMParser();
        const doc: Document = parser.parseFromString(settings.template, "text/html");

        // Add PDF view container class to widget container class list.
        doc.querySelector(this.widgetContainerSelector)?.classList?.add("pdf-view");

        const chartGraph: HTMLElement = doc.querySelector(this.chartGraphSelector);
        if (!chartGraph) return "";

        // Replace web chart content with pdf view chart content.
        chartGraph.innerHTML = settings.chart.el.nativeElement.outerHTML;

        const canvas: HTMLCanvasElement = chartGraph.querySelector("p-chart canvas");
        if (!canvas) return "";

        const image: HTMLImageElement = doc.createElement("img");

        // Replace chart canvas with an image reflecting its content.
        image.src = settings.chart.getBase64Image();
        canvas.parentNode?.replaceChild(image, canvas);

        // Fix image size.
        image.style.width = settings.chart.width;
        image.style.height = settings.chart.height;

        // Restore chart to old status.
        chart.options.devicePixelRatio = null;
        chart.resize();
        chart.update();

        return doc.body?.innerHTML;
    }

    private getCssRules(): string {
        const containerCssRules: string = this.htmlRenderingService.getCssRules(this.widgetContainerSelector);
        const bodyCssStyles: string = this.htmlRenderingService.getCssRules("body");
        return `${containerCssRules}\n${bodyCssStyles}`;
    }


    private getFooterTemplate(): string {
        return "<div class='widget-chart-footer pdf-view'>Page <span class='pageNumber'>" +
            "</span> of <span class='totalPages'></span></div>";
    }

    private getFooterCssRules(): string {
        return this.htmlRenderingService.getCssRules(".widget-chart-footer.pdf-view");
    }


    private get widgetContainerSelector(): string {
        return `.${this.widgetChartAuxiliaryService.widgetContainerClass}`;
    }

    private get chartGraphSelector(): string {
        return `.${this.widgetChartAuxiliaryService.chartGraphClass}`;
    }
}